import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Minus } from 'lucide-react';
import styles from './ThinkingTrace.module.css';
import {
  formatDuration,
  isTraceLive,
  thinkingDurationMs,
  TraceState,
} from './traceState';

/** Re-render once a second while `active` so the elapsed time ticks. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

/**
 * "How Ako is thinking": a collapsible, live list of learner-safe steps
 * (spinner -> check). Open while Ako works, auto-collapses the moment the
 * answer starts unless the learner toggled it themselves. Only the single
 * current step is announced (polite); the list itself is not a live region,
 * so assistive tech is not read every step twice.
 */
const ThinkingTrace: React.FC<{ trace: TraceState }> = ({ trace }) => {
  const { t } = useTranslation();
  const listId = useId();
  const live = isTraceLive(trace);
  const thinking = trace.phase === 'thinking' || trace.phase === 'tool';
  const now = useNow(thinking);
  const [open, setOpen] = useState(true);
  const touched = useRef(false);

  // Auto-collapse once the answer starts, unless the learner took control.
  useEffect(() => {
    if (!touched.current && trace.thinkingEndedAt !== null) setOpen(false);
  }, [trace.thinkingEndedAt]);
  // A fresh turn re-opens.
  useEffect(() => {
    if (trace.phase === 'thinking' && trace.steps.length === 0) {
      touched.current = false;
      setOpen(true);
    }
  }, [trace.phase, trace.steps.length, trace.startedAt]);

  if (trace.phase === 'idle') return null;
  if (!live && trace.steps.length === 0) return null;

  const seconds = thinkingDurationMs(trace, now);
  const duration = seconds >= 1000 ? formatDuration(seconds) : '';
  const active = trace.steps.find((s) => s.status === 'active');

  let title: string;
  if (thinking) title = t('tutor.trace.title', 'How Ako is thinking');
  else if (trace.phase === 'stopped') title = t('tutor.trace.stopped', 'Stopped');
  else if (trace.phase === 'error') title = t('tutor.trace.interrupted', 'Interrupted');
  else if (duration) title = t('tutor.trace.thoughtFor', { defaultValue: 'Thought for {{time}}', time: duration });
  else title = t('tutor.trace.title', 'How Ako is thinking');

  return (
    <section className={styles.trace} data-phase={trace.phase} aria-label={t('tutor.trace.title', 'How Ako is thinking')}>
      <button
        type="button"
        className={styles.header}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          touched.current = true;
          setOpen((v) => !v);
        }}
      >
        <ChevronRight className={styles.chevron} size={14} aria-hidden />
        <span className={styles.title}>{title}</span>
        {thinking && duration && <span className={styles.time}>{duration}</span>}
      </button>

      {/* One quiet announcement: the current step only, never the whole list. */}
      <span className={styles.srOnly} role="status" aria-live="polite">
        {thinking ? (active?.label ?? t('tutor.status.thinking', 'Thinking…')) : ''}
      </span>

      <ol id={listId} className={styles.steps} hidden={!open}>
        {trace.steps.map((step) => (
          <li key={step.id} className={styles.step} data-status={step.status}>
            <span className={styles.icon} aria-hidden>
              {step.status === 'active' ? (
                <span className={styles.spinner} />
              ) : step.status === 'done' ? (
                <Check size={12} />
              ) : (
                <Minus size={12} />
              )}
            </span>
            <span className={styles.label}>{step.label}</span>
            <span className={styles.srOnly}>
              {step.status === 'active'
                ? t('tutor.trace.inProgress', 'in progress')
                : step.status === 'done'
                  ? t('tutor.trace.complete', 'done')
                  : t('tutor.trace.skipped', 'stopped')}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
};

export default ThinkingTrace;
